import * as React from 'react'

export type ThemePreference = 'light' | 'dark' | 'system'

export const THEME_STORAGE_KEY = 'theme'

type ProviderValue = {
  preference: ThemePreference
  setPreference: (preference: ThemePreference) => void
}

const ThemeContext = React.createContext<ProviderValue>({
  preference: 'system',
  setPreference: () => { /* noop */ },
})

const readStoredPreference = (): ThemePreference => {
  if (typeof window === 'undefined') return 'system'
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
    if (stored === 'light' || stored === 'dark') return stored
  } catch {
    // Storage unavailable (private mode) — fall back to the system theme.
  }
  return 'system'
}

const systemPrefersDark = () => (
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-color-scheme: dark)').matches
)

const applyTheme = (preference: ThemePreference) => {
  const dark = preference === 'dark' || (preference === 'system' && systemPrefersDark())
  document.documentElement.classList.toggle('dark', dark)
}

type Props = {
  children?: React.ReactNode
}

export const ThemeProvider: React.FC<Props> = ({ children }) => {
  const [preference, setPreferenceState] = React.useState<ThemePreference>(readStoredPreference)

  const setPreference = React.useCallback((next: ThemePreference) => {
    setPreferenceState(next)
    try {
      if (next === 'system') {
        window.localStorage.removeItem(THEME_STORAGE_KEY)
      } else {
        window.localStorage.setItem(THEME_STORAGE_KEY, next)
      }
    } catch {
      // Storage unavailable — the choice still applies for this visit.
    }
    applyTheme(next)
  }, [])

  // Apply on mount: the inline head script (gatsby-ssr) already set the
  // class before paint, but hydration after a client-side store change and
  // the dev server both need the provider to be authoritative too.
  React.useEffect(() => {
    applyTheme(preference)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Follow OS theme changes live while on the system preference.
  React.useEffect(() => {
    if (preference !== 'system') return
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => applyTheme('system')
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [preference])

  const value = React.useMemo(
    () => ({ preference, setPreference }),
    [preference, setPreference]
  )

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

export const useTheme = (): ProviderValue => React.useContext(ThemeContext)
